import { LegendComponentOption } from 'echarts';
import EChartsReact from 'echarts-for-react';
import { isArray } from 'lodash';
import {
    Dispatch,
    RefObject,
    SetStateAction,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';

type MakeChartsOption<Series> = Omit<echarts.EChartsOption, 'series'> & {
    legend: LegendOption;
    series: (Series & { name: string; color: string })[];
};

export type EChartsOption<Series = Pick<echarts.EChartsOption, 'series'>> =
    MakeChartsOption<Series>;

export type LegendClickEvent = {
    selected: {
        [name: string]: boolean;
    };
};

type LegendOption = LegendComponentOption & { showCustom?: boolean };

type ToggleLegend = {
    type: 'TOGGLE_LEGEND';
    payload: { name: string; value?: boolean };
};

type SetData = {
    type: 'SET_DATA';
    payload: Partial<GraphState>;
};

type GraphState = {
    legend: { name: string; selected: boolean; color?: string }[];
};

const graphReducer = (state: GraphState, action: ToggleLegend | SetData) => {
    switch (action.type) {
        case 'TOGGLE_LEGEND':
            return {
                ...state,
                legend: state.legend.map((item) => {
                    if (item.name === action.payload.name) {
                        return {
                            ...item,
                            selected:
                                action.payload.value !== undefined
                                    ? action.payload.value
                                    : !item.selected,
                        };
                    }
                    return item;
                }),
            };
        case 'SET_DATA':
            return {
                ...state,
                ...action.payload,
            };
        default:
            return state;
    }
};

function getLegendItems<
    T extends EChartsOption<{ color?: string; name: string }>,
>(chartsOption: T) {
    const { legend, series } = chartsOption;

    const legendData: {
        name: string;
        selected: boolean;
        color?: string;
    }[] = [];

    if (legend && Array.isArray(series)) {
        if (legend.showCustom)
            series.forEach((s) => {
                if (s.name) {
                    legendData.push({
                        name: s.name,
                        selected: true,
                        color: s.color,
                    });
                }
            });
    }

    return legendData;
}

export type ChartRef = {
    getEchartsInstance: () => echarts.ECharts | undefined;
    getLegendItems: () => { name: string; selected: boolean; color?: string }[];
    toggleLegend: (name: string, markSelected?: boolean) => void;
};

type MouseHandlerContext = {
    seriesName: string;
    componentType: string;
    seriesIndex: number;
    seriesId?: string;
    data: any;
};

export type ChartMouseHandler = (
    context: MouseHandlerContext,
    state: {
        instance: echarts.ECharts;
        selected: string;
        setSelected: Dispatch<SetStateAction<string>>;
    },
) => void;

export type SelectedChangeHandler<
    T extends MakeChartsOption<{ color: string; name: string }> = any,
> = (state: {
    selected: string;
    instance: echarts.ECharts;
    series: T[];
}) => void;

export type ResizedHandler = (instance: echarts.ECharts) => void;

export type GlobalOutHandler = (instance: echarts.ECharts) => void;

/**
 * `echarts-for-react` creates the ECharts instance synchronously but only
 * applies `option` *after* an `await` (see `renderNewEcharts` -> it awaits
 * `initEchartsInstance`, which inits a throwaway instance, waits for
 * `finished`, disposes it and re-inits). Effects running in the same React
 * commit therefore get hold of an instance that exists but holds no option at
 * all.
 *
 * Pushing a partial option that contains cartesian series (`bar`/`line`) into
 * such an instance makes ECharts throw `xAxis "0" not found`, because the
 * series asks for `xAxis` index 0 and no axis component exists yet.
 *
 * `getOption()` returns `undefined` until the first `setOption` has landed,
 * which makes it a reliable "is this instance configured yet" probe. It also
 * covers the dispose/re-init gap that a `theme` or `opts` change reopens.
 */
export const getConfiguredInstance = (chart: EChartsReact | null) => {
    const instance = chart?.getEchartsInstance();
    if (!instance || instance.isDisposed() || !instance.getOption()) {
        return undefined;
    }
    return instance;
};

export const useChart = <
    T extends MakeChartsOption<{ color: string; name: string }>,
>({
    chartRef,
    chartOptions,
    onClick,
    onMouseOver,
    onMouseOut,
    onGlobalOut,
    onSelectedChange,
    onResize,
}: {
    chartRef?: RefObject<ChartRef>;
    chartOptions: T;
    onClick?: ChartMouseHandler;
    onMouseOver?: ChartMouseHandler;
    onMouseOut?: ChartMouseHandler;
    onGlobalOut?: GlobalOutHandler;
    onSelectedChange?: SelectedChangeHandler;
    onResize?: ResizedHandler;
}) => {
    const ref = useRef<EChartsReact>(null);
    const [selected, setSelected] = useState<string>('');
    const selectedRef = useRef(selected);

    // ECharts fires `finished` once a render pass completes, which can only
    // happen after `echarts-for-react` has applied the option. Tracking it in
    // state re-runs the effects below that were skipped while the instance was
    // still unconfigured. `echarts-for-react` awaits this same event during
    // init, so it is exactly as reliable as the library's own setup.
    const [isChartReady, setIsChartReady] = useState(false);
    const onChartFinished = useCallback(() => {
        // Verified rather than trusted: a `theme` or `opts` change makes
        // `echarts-for-react` dispose and re-init while this handler is already
        // bound, which reopens the option-less window. `finished` fires again on
        // every later render pass, so ignoring an early one costs nothing.
        if (getConfiguredInstance(ref.current)) {
            setIsChartReady(true);
        }
    }, []);

    useEffect(() => {
        selectedRef.current = selected;
    }, [selected]);

    const [{ legend }, dispatch] = useReducer(graphReducer, {
        legend: [],
    });

    const manualToggleLegend = useCallback(
        (name: string, selected: boolean) => {
            dispatch({
                type: 'TOGGLE_LEGEND',
                payload: {
                    name,
                    value: selected,
                },
            });
        },
        [],
    );

    const onLegendChange = useCallback((params: LegendClickEvent) => {
        Object.entries(params.selected).forEach(([name, value]) => {
            dispatch({
                type: 'TOGGLE_LEGEND',
                payload: {
                    name,
                    value,
                },
            });
        });
    }, []);

    useEffect(() => {
        if (!onGlobalOut) return;
        // Gated on readiness rather than binding to whatever instance exists at
        // mount: `echarts-for-react` inits a throwaway instance and disposes it
        // again (see `getConfiguredInstance`), so a handler bound during the
        // mount commit is attached to a dead object and never fires.
        const instance = getConfiguredInstance(ref.current);
        if (!instance) return;

        const handler = () => onGlobalOut(instance);

        instance.on('globalout', handler);

        return () => {
            if (!instance.isDisposed()) {
                instance.off('globalout', handler);
            }
        };
    }, [ref, onGlobalOut, isChartReady]);

    useEffect(() => {
        if (!onSelectedChange) return;
        // Must not run against an instance that has no option yet — handlers
        // push a partial option containing cartesian series, which would throw
        // `xAxis "0" not found`. Re-runs once `isChartReady` flips.
        const eCharts = getConfiguredInstance(ref.current);

        const series =
            chartOptions?.series && isArray(chartOptions.series)
                ? chartOptions.series
                : [];

        if (eCharts) {
            onSelectedChange({ selected, series, instance: eCharts });
        }
    }, [chartOptions.series, onSelectedChange, selected, isChartReady]);

    const toggleLegend = useCallback((name: string, markSelected?: boolean) => {
        if (markSelected !== undefined) {
            if (!markSelected && selectedRef.current === name) {
                setSelected('');
            }
            dispatch({
                type: 'TOGGLE_LEGEND',
                payload: {
                    name,
                    value: markSelected,
                },
            });
            ref.current?.getEchartsInstance()?.dispatchAction({
                type: markSelected ? 'legendSelect' : 'legendUnSelect',
                name,
            });
            return;
        }

        ref.current?.getEchartsInstance()?.dispatchAction({
            type: 'legendToggleSelect',
            name,
        });
    }, []);

    const createHandler = useCallback((handler: ChartMouseHandler) => {
        return (context: MouseHandlerContext, instance: echarts.ECharts) =>
            handler(context, {
                instance,
                selected: selectedRef.current,
                setSelected,
            });
    }, []);

    const onEvents = useMemo(() => {
        return {
            ...(onClick ? { click: createHandler(onClick) } : {}),
            ...(onMouseOver ? { mouseover: createHandler(onMouseOver) } : {}),
            ...(onMouseOut ? { mouseout: createHandler(onMouseOut) } : {}),
            legendselectchanged: onLegendChange,
            finished: onChartFinished,
        };
    }, [
        createHandler,
        onClick,
        onLegendChange,
        onMouseOut,
        onMouseOver,
        onChartFinished,
    ]);

    useEffect(() => {
        dispatch({
            type: 'SET_DATA',
            payload: {
                legend: getLegendItems(chartOptions),
            },
        });
    }, [chartOptions]);

    useEffect(() => {
        // Same readiness gate as the `globalout` handler above: bound to the
        // throwaway mount instance this never fired, which left "click empty
        // chart space to clear the selection" silently dead on first mount.
        const zr = getConfiguredInstance(ref.current)?.getZr();
        if (!zr) return;

        const handler = (e: { target: unknown | undefined }) => {
            if (selectedRef.current && !e.target) setSelected('');
        };

        zr.on('click', handler);

        return () => {
            zr.off('click', handler);
        };
    }, [ref, isChartReady]);

    useEffect(() => {
        // Resolved per call, not captured: `echarts-for-react` throws away the
        // instance it creates first and re-inits, so an instance captured at
        // effect time is already disposed by the time this listener fires.
        const listener = () => {
            const eCharts = getConfiguredInstance(ref.current);
            if (!eCharts) return;
            eCharts.resize();
            if (onResize) onResize(eCharts);
        };

        const resizeObserver = new ResizeObserver(() => {
            listener();
        });

        const container = ref.current?.getEchartsInstance()?.getDom();
        if (container) {
            resizeObserver.observe(container);
        }

        window.addEventListener('resize', listener);

        return () => {
            window.removeEventListener('resize', listener);
            resizeObserver.disconnect();
        };
    }, [onResize, ref, isChartReady]);

    const combinedChartOptions = useMemo(() => {
        return {
            ...chartOptions,
            legend: {
                ...chartOptions.legend,
                show: chartOptions.legend?.showCustom
                    ? false
                    : chartOptions.legend?.show,
            },
        };
    }, [chartOptions]);

    useImperativeHandle(chartRef, () => {
        return {
            getEchartsInstance: () => ref.current?.getEchartsInstance(),
            getLegendItems: () => legend,
            toggleLegend,
        };
    }, [legend, toggleLegend]);

    return {
        ref,
        manualToggleLegend,
        legendItems: legend,
        toggleLegend,
        chartOptions: combinedChartOptions,
        onLegendChange,
        onEvents,
        selected,
        setSelected,
        /**
         * `false` until the chart has an option applied. Include it in the deps
         * of any effect that reaches for the instance imperatively, so the
         * effect re-runs once the instance is safe to talk to.
         */
        isChartReady,
        /**
         * Pass to `BaseChart` as `onChartReady`. `echarts-for-react` invokes it
         * immediately after applying the option, so it is the earliest
         * guaranteed signal that imperative `setOption` calls are safe. The
         * `finished` entry in `onEvents` covers the same ground for charts that
         * do not wire this up — it is bound after the option lands, so it only
         * reports later render passes — but prefer this prop.
         */
        onChartReady: onChartFinished,
    };
};
